import { useState } from 'react';
import {
  getBidSoundPreset,
  isBidSoundMuted,
  playBidSound,
  setBidSoundMuted,
  setBidSoundPreset,
  SOUND_PRESETS,
  type SoundPresetId,
} from '../../lib/bidSound';

/**
 * The bid-sound switch and the sound picker — ONE control for the lot page and
 * the Lots page (Jim, 2026-10-07: the Lots page gets "the same sound dropdown
 * as the lot page").
 *
 * It lived inside `PublicLotDetail`, beside the bid history. A second copy on
 * the Lots page would be two controls writing the same two settings, free to
 * drift in wording and behaviour; this one owns them. The settings are the
 * browser's (`lib/bidSound`), so a choice made on one page holds on the other.
 *
 * Turning the sound on, or picking a sound, plays it once: you hear what you
 * chose, and the click is the gesture browsers require before a page may make
 * a sound at all.
 */
export function SoundControls() {
  const [muted, setMuted] = useState<boolean>(() => isBidSoundMuted());
  const [preset, setPreset] = useState<SoundPresetId>(() => getBidSoundPreset());

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} data-testid="sound-controls">
      <button
        type="button"
        onClick={() => {
          const next = !muted;
          setMuted(next);
          setBidSoundMuted(next);
          // Unmuting doubles as the user-gesture that unlocks audio.
          if (!next) playBidSound({ bypassMute: true });
        }}
        className="w-chip"
        aria-pressed={!muted}
        aria-label={muted ? 'Turn bid sound on' : 'Turn bid sound off'}
      >
        {muted ? '🔇' : '🔊'} Sound
      </button>
      <select
        value={preset}
        onChange={(e) => {
          const next = e.target.value as SoundPresetId;
          setPreset(next);
          setBidSoundPreset(next);
          // Preview the choice — even while muted, so the choice can be heard.
          playBidSound({ preset: next, bypassMute: true });
        }}
        aria-label="Bid sound style"
        style={{
          minHeight: 38,
          padding: '0 10px',
          borderRadius: 999,
          border: '1.5px solid var(--border)',
          background: 'var(--surface-elev)',
          color: 'var(--text-muted)',
          fontSize: 13.5,
        }}
      >
        {SOUND_PRESETS.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
    </div>
  );
}
